import React, { useEffect, useRef, memo, useState } from 'react';
import Chart from 'chart.js/auto';
import './ConsumptionChart.css';
import { getChartDataForTenant } from '../data/consumptionData';

const ConsumptionChart = memo(({ consumption, status, accountKey, accountName, contract }) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const [selectedPeriod, setSelectedPeriod] = useState('last12months');
  
  // Get Data Assure chart data
  const dataAssureData = getChartDataForTenant(accountKey);
  
  // Calculate metrics from Data Assure data
  const calculateMetrics = () => {
    if (!dataAssureData) return { 
      monthlyAvg: 0, 
      totalValidations: 0, 
      trend: 'new', 
      trendDetail: 'New Customer',
      growthRate: 0, 
      currentMonthProjection: null,
      projectionWarning: false 
    };
    
    const totals = dataAssureData.tenantInfo.totalCounts;
    const total = totals.failed + totals.passed + totals.passedWithCautions;
    
    // Get current date to check for partial months
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;
    const currentDay = currentDate.getDate();
    const daysInMonth = new Date(currentYear, currentMonth, 0).getDate();
    
    // Check if the last month in data is the current partial month
    const labels = dataAssureData.labels;
    const lastLabel = labels[labels.length - 1];
    const isLastMonthPartial = lastLabel && lastLabel.includes(`${currentDate.toLocaleString('default', { month: 'short' })} ${currentYear}`) && currentDay < daysInMonth;
    
    // Separate complete months from partial month
    const completeMonthsData = isLastMonthPartial 
      ? dataAssureData.totalData.slice(0, -1)
      : dataAssureData.totalData;
    
    // Calculate current month projection if we have a partial month
    let currentMonthProjection = null;
    let projectionWarning = false;
    
    if (isLastMonthPartial) {
      const lastMonthActual = dataAssureData.totalData[dataAssureData.totalData.length - 1];
      const dailyAvg = lastMonthActual / currentDay;
      currentMonthProjection = Math.round(dailyAvg * daysInMonth);
    }
    
    // Handle different data availability scenarios
    const numCompleteMonths = completeMonthsData.length;
    let trend = 'stable';
    let trendDetail = '';
    let growthRate = 0;
    let monthlyAvg = 0;
    
    if (numCompleteMonths === 0) {
      // New customer with only partial current month or no data
      trend = 'new';
      trendDetail = 'New Customer';
      monthlyAvg = currentMonthProjection || 0;
    } else if (numCompleteMonths === 1) {
      // Only one complete month - still considered new
      trend = 'new';
      trendDetail = 'New Customer (1 month)';
      monthlyAvg = completeMonthsData[0];
    } else if (numCompleteMonths === 2) {
      // Two months - compare month over month
      const month1 = completeMonthsData[0];
      const month2 = completeMonthsData[1];
      growthRate = month1 > 0 ? ((month2 - month1) / month1 * 100) : 0;
      
      if (growthRate > 10) {
        trend = 'increasing';
        trendDetail = 'Month-over-Month';
      } else if (growthRate < -10) {
        trend = 'decreasing';
        trendDetail = 'Month-over-Month';
      } else {
        trend = 'stable';
        trendDetail = 'Month-over-Month';
      }
      
      monthlyAvg = Math.round((month1 + month2) / 2);
    } else if (numCompleteMonths >= 3 && numCompleteMonths < 6) {
      // 3-5 months: Compare recent 3 months vs all available
      const recent3Months = completeMonthsData.slice(-3);
      const recent3Avg = recent3Months.reduce((a, b) => a + b, 0) / 3;
      const allMonthsAvg = completeMonthsData.reduce((a, b) => a + b, 0) / numCompleteMonths;
      
      growthRate = allMonthsAvg > 0 ? ((recent3Avg - allMonthsAvg) / allMonthsAvg * 100) : 0;
      
      if (growthRate > 10) {
        trend = 'increasing';
        trendDetail = `3mo vs ${numCompleteMonths}mo avg`;
      } else if (growthRate < -10) {
        trend = 'decreasing';
        trendDetail = `3mo vs ${numCompleteMonths}mo avg`;
      } else {
        trend = 'stable';
        trendDetail = `3mo vs ${numCompleteMonths}mo avg`;
      }
      
      monthlyAvg = Math.round(allMonthsAvg);
    } else {
      // 6+ months: Compare recent 3 months vs recent 6 months
      const recent3Months = completeMonthsData.slice(-3);
      const recent6Months = completeMonthsData.slice(-6);
      const recent3Avg = recent3Months.reduce((a, b) => a + b, 0) / 3;
      const recent6Avg = recent6Months.reduce((a, b) => a + b, 0) / 6;
      
      growthRate = recent6Avg > 0 ? ((recent3Avg - recent6Avg) / recent6Avg * 100) : 0;
      
      if (growthRate > 10) {
        trend = 'increasing';
        trendDetail = '3mo vs 6mo avg';
      } else if (growthRate < -10) {
        trend = 'decreasing';
        trendDetail = '3mo vs 6mo avg';
      } else {
        trend = 'stable';
        trendDetail = '3mo vs 6mo avg';
      }
      
      monthlyAvg = Math.round(recent6Avg);
    }
    
    // Check if current month projection is below 3-month average
    if (currentMonthProjection !== null && numCompleteMonths >= 3) {
      const recent3Months = completeMonthsData.slice(-3);
      const recent3Avg = recent3Months.reduce((a, b) => a + b, 0) / 3;
      projectionWarning = currentMonthProjection < (recent3Avg * 0.8); // Warn if 20% below average
    }
    
    return { 
      monthlyAvg, 
      totalValidations: total, 
      trend, 
      trendDetail,
      growthRate, 
      currentMonthProjection,
      projectionWarning 
    };
  };
  
  const metrics = calculateMetrics();

  useEffect(() => {
    if (!canvasRef.current || !dataAssureData) {
      return;
    }
    
    const ctx = canvasRef.current.getContext('2d');
    
    // Destroy existing chart if it exists
    if (chartRef.current) {
      chartRef.current.destroy();
    }
    
    // Filter data based on selected period
    let filteredData = dataAssureData;
    if (selectedPeriod === 'last12months') {
      const numDatasets = filteredData.datasets[0].data.length;
      const startIndex = Math.max(0, numDatasets - 12);
      
      filteredData = {
        ...dataAssureData,
        labels: dataAssureData.labels.slice(startIndex),
        datasets: dataAssureData.datasets.map(dataset => ({
          ...dataset,
          data: dataset.data.slice(startIndex)
        })),
        totalData: dataAssureData.totalData.slice(startIndex)
      };
    }
    
    // Check if we need to add projection for current month
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;
    const currentDay = currentDate.getDate();
    const daysInMonth = new Date(currentYear, currentMonth, 0).getDate();
    const lastLabel = filteredData.labels[filteredData.labels.length - 1];
    const isLastMonthCurrent = lastLabel && lastLabel.includes(`${currentDate.toLocaleString('default', { month: 'short' })} ${currentYear}`);
    
    let projectedTotal = 0;
    let customPlugin = null;
    
    if (isLastMonthCurrent && currentDay < daysInMonth) {
      // Calculate projection for current month
      const lastMonthTotal = filteredData.totalData[filteredData.totalData.length - 1];
      const dailyAvg = lastMonthTotal / currentDay;
      projectedTotal = Math.round(dailyAvg * daysInMonth);
      
      // Create custom plugin to draw dashed outline
      customPlugin = {
        id: 'projectionOutline',
        afterDatasetsDraw: function(chart) {
          const ctx = chart.ctx;
          const meta = chart.getDatasetMeta(0);
          if (!meta || !meta.data || meta.data.length === 0) return;
          
          // Get the last bar (current month) from first dataset
          const lastBarIndex = filteredData.labels.length - 1;
          const lastBar = meta.data[lastBarIndex];
          if (!lastBar) return;
          
          // Calculate projected height
          const yScale = chart.scales.y;
          const projectedY = yScale.getPixelForValue(projectedTotal);
          const baseY = yScale.getPixelForValue(0);
          
          // Draw dashed rectangle around projection area
          ctx.save();
          ctx.strokeStyle = 'rgba(255, 107, 53, 0.7)'; // Relish orange
          ctx.lineWidth = 2.5;
          ctx.setLineDash([6, 4]);
          
          // Draw rectangle from base to projected height
          const barWidth = lastBar.width || 60; // Fallback width
          const x = lastBar.x - barWidth / 2;
          const height = baseY - projectedY;
          
          // Draw the dashed outline box
          ctx.strokeRect(x - 1, projectedY - 1, barWidth + 2, height + 2);
          
          // Fill the area above current total with light green (projection area)
          const currentTotal = filteredData.totalData[lastBarIndex];
          const currentY = yScale.getPixelForValue(currentTotal);
          if (projectedTotal > currentTotal) {
            ctx.fillStyle = 'rgba(255, 107, 53, 0.15)';
            ctx.fillRect(x, projectedY, barWidth, currentY - projectedY);
          }
          
          ctx.restore();
        }
      };
    }
    
    // Use original datasets without modification
    const allDatasets = filteredData.datasets;
    
    chartRef.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: filteredData.labels,
        datasets: allDatasets
      },
      plugins: customPlugin ? [customPlugin] : [],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            grid: {
              display: false
            }
          },
          y: {
            beginAtZero: true,
            ticks: {
              callback: function(value) {
                return value.toLocaleString();
              }
            }
          }
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
              label: function(context) {
                return 'Validations: ' + context.parsed.y.toLocaleString();
              },
              footer: function(tooltipItems) {
                const dataIndex = tooltipItems[0].dataIndex;
                
                // Check if this is the current month with projection
                if (isLastMonthCurrent && dataIndex === filteredData.labels.length - 1 && currentDay < daysInMonth) {
                  return [
                    '',
                    'Projected Total: ' + projectedTotal.toLocaleString(),
                    `(Day ${currentDay} of ${daysInMonth})`
                  ];
                }
                
                return '';
              }
            }
          }
        }
      }
    });
    
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
      }
    };
  }, [selectedPeriod, dataAssureData]);

  if (!dataAssureData) {
    return (
      <div className="consumption-container">
        <div className="consumption-header">
          <h3>Data Assure Consumption</h3>
        </div>
        <div className="no-data-message">
          No consumption data available for this account.
        </div>
      </div>
    );
  }

  return (
    <div className="consumption-container">
      <div className="consumption-header">
        <h3>Data Assure Consumption</h3>
        <div className="year-selector-container">
          <select 
            value={selectedPeriod} 
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="year-selector"
          >
            <option value="all">All Time</option>
            <option value="last12months">Last 12 Months</option>
          </select>
        </div>
      </div>
      
      <div className="usage-info">
        <div className="usage-stat">
          <span className="usage-label">Total Validations</span>
          <span className="usage-value">{metrics.totalValidations.toLocaleString()}</span>
        </div>
        <div className="usage-stat">
          <span className="usage-label">Monthly Average</span>
          <span className="usage-value">
            {metrics.monthlyAvg.toLocaleString()}
          </span>
        </div>
        <div className="usage-stat">
          <span className="usage-label">Volume Trend</span>
          {metrics.trend === 'new' ? (
            <span className="usage-value info">
              {metrics.trendDetail}
            </span>
          ) : (
            <span className={`usage-value ${metrics.trend === 'increasing' ? 'success' : metrics.trend === 'decreasing' ? 'warning' : 'neutral'}`}>
              {metrics.trend === 'increasing' ? '↑' : metrics.trend === 'decreasing' ? '↓' : '→'} 
              {Math.abs(metrics.growthRate).toFixed(0)}% 
              <span className="trend-detail">({metrics.trendDetail})</span>
            </span>
          )}
        </div>
      </div>

      {metrics.projectionWarning && (
        <div className="consumption-alert warning">
          <span className="alert-icon">⚠️</span>
          <div className="alert-content">
            <strong>Current Month Tracking Below Average</strong>
            <p>Current month is projecting {metrics.currentMonthProjection?.toLocaleString()} validations, which is below the 3-month average. Consider checking in with the customer.</p>
          </div>
        </div>
      )}
      
      {metrics.trend === 'decreasing' && metrics.growthRate < -20 && (
        <div className="consumption-alert info">
          <span className="alert-icon">📊</span>
          <div className="alert-content">
            <strong>Validation Volume Declining</strong>
            <p>Validation activity has decreased by {Math.abs(metrics.growthRate).toFixed(0)}% ({metrics.trendDetail}). Consider checking in with the customer about their usage patterns.</p>
          </div>
        </div>
      )}
      
      {metrics.trend === 'increasing' && metrics.growthRate > 30 && (
        <div className="consumption-alert success">
          <span className="alert-icon">📈</span>
          <div className="alert-content">
            <strong>Strong Validation Growth</strong>
            <p>Validation activity has increased by {metrics.growthRate.toFixed(0)}% ({metrics.trendDetail}). The customer is actively using the platform!</p>
          </div>
        </div>
      )}
      
      {metrics.trend === 'new' && (
        <div className="consumption-alert neutral">
          <span className="alert-icon">🆕</span>
          <div className="alert-content">
            <strong>New Customer</strong>
            <p>Not enough historical data to establish trends. Continue monitoring as more data becomes available.</p>
          </div>
        </div>
      )}

      <div className="chart-container">
        <canvas ref={canvasRef}></canvas>
      </div>

      {metrics.currentMonthProjection && (
        <div className="consumption-footer">
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: '#ff6b35' }}></span>
            <span>Current Month Actual</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: 'rgba(255, 107, 53, 0.3)', border: '2px dashed #ff6b35', boxSizing: 'border-box' }}></span>
            <span>Projected by Month End</span>
          </div>
        </div>
      )}
    </div>
  );
});

export default ConsumptionChart;